import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import "./AddSchool.css";

function AddSchool({ onSchoolAdded, onCancel, schoolData, isEditMode }) {
  const [loading, setLoading] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [useCustomFees, setUseCustomFees] = useState(false);

  // Term Structure States
  const [termStructures, setTermStructures] = useState([]);
  const [selectedTermStructure, setSelectedTermStructure] = useState("");
  const [loadingTermStructures, setLoadingTermStructures] = useState(true);
  const [defaultAdminFee, setDefaultAdminFee] = useState("160");
  const [formData, setFormData] = useState({
    schoolName: "",
    schoolCode: "",
    schoolEmail: "",
    schoolPhone: "",
    schoolAddress: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    contactRole: "",
    notes: "",

    // Admin fee and instalment structure
    adminFee: defaultAdminFee,
    instalmentType: "3",
    lessonsPerTerm: "",

    // Custom fees (only used if useCustomFees is true)
    customTermFee: "",
    customInstalmentFee: "",
    customTermType: "4",
    lessonDay: "",
    lessonTime: "",
    commissionType: "none",
    commissionValue: "",
  });

  useEffect(() => {
    loadPackagesFromMarketingOrders();
    loadTermStructures();
    loadDefaultAdminFee();  // â† ADD THIS CALL
  }, []);

  // NEW FUNCTION - Fetch default admin fee from Admin Fee Structure
  const loadDefaultAdminFee = async () => {
    try {
      const adminFeeDoc = await getDoc(doc(db, 'adminFeeStructure', 'current'));
      
      if (adminFeeDoc.exists()) {
        const data = adminFeeDoc.data();
        const defaultFee = data.defaultAdminFee || 160;
        setDefaultAdminFee(defaultFee.toString());
      } else {
        // If no admin fee structure exists yet, use 160 as fallback
        setDefaultAdminFee("160");
      }
    } catch (error) {
      console.error('Error loading default admin fee:', error);
      // Fallback to 160 if there's an error
      setDefaultAdminFee("160");
    }
  };
  // Update formData.adminFee when defaultAdminFee changes (but only for new schools)
  useEffect(() => {
    if (!isEditMode && !schoolData) {
      setFormData(prev => ({
        ...prev,
        adminFee: defaultAdminFee
      }));
    }
  }, [defaultAdminFee, isEditMode, schoolData]);

  useEffect(() => {
    if (selectedTermStructure && termStructures.length > 0) {
      const structure = termStructures.find(
        (s) => s.id === selectedTermStructure
      );
      if (structure && structure.lessonsPerTerm) {
        // Only auto-fill lessonsPerTerm if it's empty (not in edit mode with existing value)
        setFormData((prev) => ({
          ...prev,
          lessonsPerTerm: prev.lessonsPerTerm || structure.lessonsPerTerm.toString(),
        }));
      }
    }
  }, [selectedTermStructure, termStructures]);
  // ADD THIS NEW useEffect HERE
  useEffect(() => {
    // Auto-select term structure when custom term type changes
    if (useCustomFees && formData.customTermType) {
      autoSelectTermStructure(parseInt(formData.customTermType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customTermType, useCustomFees]);
  // Existing useEffects are here...

  // Populate form when editing
  useEffect(() => {
    if (schoolData) {
      // Check if it's a prospect being activated
      if (schoolData.schoolStatus === 'prospect') {
        // Pre-fill form with prospect data
        setFormData({
          schoolName: schoolData.schoolName || "",
          schoolCode: schoolData.schoolName?.substring(0, 2).toUpperCase() || "",
          schoolEmail: schoolData.prospectInfo?.contactEmail || "",
          schoolPhone: schoolData.prospectInfo?.contactPhone || "",
          schoolAddress: schoolData.prospectInfo?.address || "",
          contactName: schoolData.prospectInfo?.contactPerson || "",
          contactEmail: schoolData.prospectInfo?.contactEmail || "",
          contactPhone: schoolData.prospectInfo?.contactPhone || "",
          contactRole: "",
          notes: schoolData.prospectInfo?.notes || "",
          adminFee: defaultAdminFee,
          instalmentType: "3",
          lessonsPerTerm: "",
          customTermFee: "",
          customInstalmentFee: "",
          customTermType: "4",
          lessonDay: "",
          lessonTime: "",
          commissionType: "none",
          commissionValue: "",
        });
      } else if (isEditMode) {
        // Existing edit mode logic - populate form with existing school data
        setFormData({
          schoolName: schoolData.schoolName || "",
          schoolCode: schoolData.schoolCode || "",
          schoolEmail: schoolData.schoolEmail || "",
          schoolPhone: schoolData.schoolPhone || "",
          schoolAddress: schoolData.schoolAddress || "",
          contactName: schoolData.contactName || "",
          contactEmail: schoolData.contactEmail || "",
          contactPhone: schoolData.contactPhone || "",
          contactRole: schoolData.contactRole || "",
          notes: schoolData.notes || "",
          adminFee: schoolData.adminFee?.toString() || "160",
          instalmentType: schoolData.instalmentType?.toString() || "3",
          lessonsPerTerm: schoolData.lessonsPerTerm?.toString() || "",
          customTermFee: schoolData.schoolFee?.termFee?.toString() || "",
          customInstalmentFee: schoolData.schoolFee?.instalmentFee?.toString() || "",
          customTermType: schoolData.schoolFee?.termType?.toString() || "4",
          lessonDay: schoolData.lessonDay || "",
          lessonTime: schoolData.lessonTime || "",
          commissionType: schoolData.commission?.type || "none",
          commissionValue: schoolData.commission?.value?.toString() || "",
        });
      }
    }
  }, [isEditMode, schoolData]);

  // NEW separate useEffect - Set package selection AFTER packages are loaded
  useEffect(() => {
    if (isEditMode && schoolData && packages.length > 0 && !loadingPackages) {
      // Set package selection or custom fees - but only after packages are loaded
      if (schoolData.packageId && schoolData.packageId !== "custom") {
        setSelectedPackage(schoolData.packageId);
        setUseCustomFees(false);
      } else {
        setUseCustomFees(true);
        setSelectedPackage("");
      }
    }
  }, [isEditMode, schoolData, packages, loadingPackages]);

  // NEW separate useEffect - Set term structure AFTER term structures are loaded
  useEffect(() => {
    if (isEditMode && schoolData && termStructures.length > 0 && !loadingTermStructures) {
      // Set term structure if it exists - but only after term structures are loaded
      if (schoolData.termStructureRef?.id) {
        setSelectedTermStructure(schoolData.termStructureRef.id);
      }
    }
  }, [isEditMode, schoolData, termStructures, loadingTermStructures]);

  // Auto-populate Contact Email from School Email (only when field loses focus)

  const loadPackagesFromMarketingOrders = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, "Franchisees", user.uid);

      const q = query(
        collection(db, "marketingOrders"),
        where("franchiseeRef", "==", franchiseeRef),
        orderBy("orderDate", "desc"),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const orderData = snapshot.docs[0].data();
        const extractedPackages = [];

        if (orderData.c1TermFee || orderData.c1InstalmentFee) {
          extractedPackages.push({
            packageId: "pkg1",
            packageName: "4 Term Schools",
            termFee: parseFloat(orderData.c1TermFee) || 0,
            instalmentFee: parseFloat(orderData.c1InstalmentFee) || 0,
            termType: 4,
            description: orderData.c1Extra || "Standard 4-term fee structure",
          });
        }

        if (orderData.c2TermFee || orderData.c2InstalmentFee) {
          extractedPackages.push({
            packageId: "pkg2",
            packageName: "3 Term Schools",
            termFee: parseFloat(orderData.c2TermFee) || 0,
            instalmentFee: parseFloat(orderData.c2InstalmentFee) || 0,
            termType: 3,
            description: orderData.c2Extra || "Standard 3-term fee structure",
          });
        }

        if (orderData.c3TermFee || orderData.c3InstalmentFee) {
          extractedPackages.push({
            packageId: "pkg3",
            packageName: "Other Fee Structure",
            termFee: parseFloat(orderData.c3TermFee) || 0,
            instalmentFee: parseFloat(orderData.c3InstalmentFee) || 0,
            termType: orderData.c3TermType || 4,
            description: orderData.c3Extra || "Custom fee structure",
          });
        }

        setPackages(extractedPackages);

        if (extractedPackages.length > 0) {
          setSelectedPackage(extractedPackages[0].packageId);
        }
      } else {
        console.log("No marketing orders found - using custom fees");
        setUseCustomFees(true);
      }
    } catch (error) {
      console.error("Error loading packages:", error);
      setUseCustomFees(true);
    } finally {
      setLoadingPackages(false);
    }
  };

  const loadTermStructures = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, "Franchisees", user.uid);

      const q = query(
        collection(db, "configurations"),
        where("franchiseeRef", "==", franchiseeRef),
        where("configType", "==", "termStructure"),
        where("isActive", "==", true)
      );

      const snapshot = await getDocs(q);
      const structuresList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setTermStructures(structuresList);

      // Auto-select first term structure if available
      if (structuresList.length > 0) {
        setSelectedTermStructure(structuresList[0].id);
      } else {
        console.log("No term structures found");
      }
    } catch (error) {
      console.error("Error loading term structures:", error);
    } finally {
      setLoadingTermStructures(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePackageSelection = (packageId) => {
    setSelectedPackage(packageId);
    setUseCustomFees(false);

    // Auto-select matching term structure
    const selectedPkg = packages.find((pkg) => pkg.packageId === packageId);
    if (selectedPkg) {
      autoSelectTermStructure(selectedPkg.termType);
    }
  };

  const handleCustomFeesToggle = () => {
    setUseCustomFees(true);
    setSelectedPackage("");
  };
  // ADD THIS NEW FUNCTION HERE (after handleCustomFeesToggle)
  const autoSelectTermStructure = (termType) => {
    // Find a term structure that matches the term type
    const matchingStructure = termStructures.find(
      (s) => s.termType === termType
    );

    if (matchingStructure) {
      setSelectedTermStructure(matchingStructure.id);
    } else {
      setSelectedTermStructure("");
      alert(
        "No matching term structure found. Please create one or select manually."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, "Franchisees", user.uid);

      let schoolFee;
      let packageId;

      if (useCustomFees) {
        schoolFee = {
          termFee: parseFloat(formData.customTermFee) || 0,
          instalmentFee: parseFloat(formData.customInstalmentFee) || 0,
          termType: parseInt(formData.customTermType) || 4,
        };
        packageId = "custom";
      } else {
        const selectedPkg = packages.find(
          (pkg) => pkg.packageId === selectedPackage
        );
        if (!selectedPkg) {
          alert("Please select a fee structure or choose custom fees");
          setLoading(false);
          return;
        }

        schoolFee = {
          termFee: selectedPkg.termFee,
          instalmentFee: selectedPkg.instalmentFee,
          termType: selectedPkg.termType,
        };
        packageId = selectedPkg.packageId;
      }

      if (!formData.schoolCode.trim()) {
        alert("School code is required");
        setLoading(false);
        return;
      }

      // Validate term structure selection
      if (!selectedTermStructure) {
        alert("Please select a term structure for this school");
        setLoading(false);
        return;
      }

      const schoolDataToSave = {
        schoolName: formData.schoolName,
        schoolStatus: 'active',
        schoolCode: formData.schoolCode.toUpperCase().trim(),
        schoolEmail: formData.schoolEmail,
        schoolPhone: formData.schoolPhone,
        schoolAddress: formData.schoolAddress,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        contactRole: formData.contactRole,
        franchiseeRef: franchiseeRef,
        packageId: packageId,
        schoolFee: schoolFee,
        termStructureRef: doc(db, "configurations", selectedTermStructure),
        termStructureYear: new Date().getFullYear(),
        adminFee: parseFloat(formData.adminFee) || 160,
        instalmentType: parseInt(formData.instalmentType) || 3,
        lessonsPerTerm: parseInt(formData.lessonsPerTerm) || 10,
        notes: formData.notes,
        lessonDay: formData.lessonDay,
        lessonTime: formData.lessonTime,
        commission: {
          type: formData.commissionType || "none",
          value: formData.commissionType !== "none"
            ? parseFloat(formData.commissionValue) || 0
            : 0,
        },
      };

      if (isEditMode && schoolData?.id) {
        // UPDATE existing school
        await updateDoc(doc(db, "schools", schoolData.id), {
          ...schoolDataToSave,
          lastModified: new Date(),
        });
        alert("School updated successfully!");
      } else {
        // CREATE new school
        await addDoc(collection(db, "schools"), {
          ...schoolDataToSave,
          isActive: true,
          numberOfStudents: 0,
          createdDate: new Date(),
        });
        alert("School added successfully!");
      }

      onSchoolAdded();
    } catch (error) {
      console.error("Error saving school:", error);
      alert("Error saving school. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loadingPackages || loadingTermStructures) {
    return (
      <div className="add-school-container">
        <div className="add-school-form">
          <div className="loading">Loading school setup...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-school-container">
      <div className="add-school-form">
        <div className="form-header">
          <h1>{isEditMode ? "Edit School" : "Add New School"}</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>School Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label>School Name *</label>
                <input
                  type="text"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Pampoen Paleis Primary"
                />
              </div>

              <div className="form-group">
                <label>School Code * (Used for student account numbers)</label>
                <input
                  type="text"
                  name="schoolCode"
                  value={formData.schoolCode}
                  onChange={handleChange}
                  required
                  placeholder="e.g., PP"
                  maxLength="10"
                  style={{ textTransform: "uppercase" }}
                />
                <small>Keep it short (2-5 characters)</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>School Email</label>
                <input
                  type="email"
                  name="schoolEmail"
                  value={formData.schoolEmail}
                  onChange={handleChange}
                  onBlur={() => {
                    if (formData.schoolEmail && !formData.contactEmail) {
                      setFormData((prev) => ({
                        ...prev,
                        contactEmail: formData.schoolEmail,
                      }));
                    }
                  }}
                  placeholder="school@example.com"
                />
              </div>

              <div className="form-group">
                <label>School Phone</label>
                <input
                  type="tel"
                  name="schoolPhone"
                  value={formData.schoolPhone}
                  onChange={handleChange}
                  onBlur={() => {
                    if (formData.schoolPhone && !formData.contactPhone) {
                      setFormData((prev) => ({
                        ...prev,
                        contactPhone: formData.schoolPhone,
                      }));
                    }
                  }}
                  placeholder="011 234 5678"
                />
              </div>
            </div>

            <div className="form-group">
              <label>School Address</label>
              <input
                type="text"
                name="schoolAddress"
                value={formData.schoolAddress}
                onChange={handleChange}
                placeholder="123 Main Street, Johannesburg"
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Main Contact Person at School</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Contact Name</label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  placeholder="e.g., Mrs. Smith"
                />
              </div>

              <div className="form-group">
                <label>Contact Role</label>
                <input
                  type="text"
                  name="contactRole"
                  value={formData.contactRole}
                  onChange={handleChange}
                  placeholder="e.g., Principal, Owner"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Contact Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  placeholder="contact@example.com"
                />
              </div>

              <div className="form-group">
                <label>Contact Phone</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  placeholder="082 123 4567"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Fee Structure *</h2>

            {packages.length > 0 ? (
              <>
                <p className="section-note">
                  Select a fee structure from your marketing order, or choose
                  custom fees below:
                </p>

                <div className="package-selection">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.packageId}
                      className={`package-option ${
                        selectedPackage === pkg.packageId ? "selected" : ""
                      }`}
                      onClick={() => handlePackageSelection(pkg.packageId)}
                    >
                      <input
                        type="radio"
                        name="package"
                        value={pkg.packageId}
                        checked={selectedPackage === pkg.packageId}
                        onChange={() => handlePackageSelection(pkg.packageId)}
                      />
                      <div className="package-details">
                        <h3>{pkg.packageName}</h3>
                        <div className="package-fees">
                          <span>Term Fee: R{pkg.termFee}</span>
                          <span>Instalment Fee: R{pkg.instalmentFee}</span>
                          <span>{pkg.termType} Terms</span>
                        </div>
                        {pkg.description && (
                          <p className="package-description">
                            {pkg.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  <div
                    className={`package-option ${
                      useCustomFees ? "selected" : ""
                    }`}
                    onClick={handleCustomFeesToggle}
                  >
                    <input
                      type="radio"
                      name="package"
                      value="custom"
                      checked={useCustomFees}
                      onChange={handleCustomFeesToggle}
                    />
                    <div className="package-details">
                      <h3>Custom Fees</h3>
                      <p className="package-description">
                        Enter different fees for this school
                      </p>
                    </div>
                  </div>
                </div>

                {useCustomFees && (
                  <div className="custom-fees-section">
                    <h3>Enter Custom Fees</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Term Fee (R)</label>
                        <input
                          type="number"
                          name="customTermFee"
                          value={formData.customTermFee}
                          onChange={handleChange}
                          required={useCustomFees}
                          placeholder="600"
                        />
                      </div>

                      <div className="form-group">
                        <label>Instalment Fee (R)</label>
                        <input
                          type="number"
                          name="customInstalmentFee"
                          value={formData.customInstalmentFee}
                          onChange={handleChange}
                          required={useCustomFees}
                          placeholder="200"
                        />
                      </div>

                      <div className="form-group">
                        <label>Term Type</label>
                        <select
                          name="customTermType"
                          value={formData.customTermType}
                          onChange={handleChange}
                        >
                          <option value="3">3 Terms</option>
                          <option value="4">4 Terms</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="custom-fees-section">
                <p className="section-note">
                  No fee structures found in your marketing orders. Please enter
                  custom fees:
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Term Fee (R) *</label>
                    <input
                      type="number"
                      name="customTermFee"
                      value={formData.customTermFee}
                      onChange={handleChange}
                      required
                      placeholder="600"
                    />
                  </div>

                  <div className="form-group">
                    <label>Instalment Fee (R) *</label>
                    <input
                      type="number"
                      name="customInstalmentFee"
                      value={formData.customInstalmentFee}
                      onChange={handleChange}
                      required
                      placeholder="200"
                    />
                  </div>

                  <div className="form-group">
                    <label>Term Type *</label>
                    <select
                      name="customTermType"
                      value={formData.customTermType}
                      onChange={handleChange}
                      required
                    >
                      <option value="3">3 Terms</option>
                      <option value="4">4 Terms</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Term Structure *</h2>

            {termStructures.length > 0 ? (
              <>
                <p className="section-note">
                  Select the term dates structure for this school:
                </p>

                <div className="form-group">
                  <label>Term Structure Template</label>
                  <select
                    value={selectedTermStructure}
                    onChange={(e) => setSelectedTermStructure(e.target.value)}
                    required
                  >
                    <option value="">-- Select Term Structure --</option>
                    {termStructures.map((structure) => (
                      <option key={structure.id} value={structure.id}>
                        {structure.templateName} ({structure.year})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTermStructure && (
                  <div
                    style={{
                      background: "#f0f4ff",
                      border: "1px solid #4169E1",
                      borderRadius: "5px",
                      padding: "15px",
                      marginTop: "15px",
                    }}
                  >
                    <strong style={{ color: "#4169E1" }}>
                      Selected Term Structure:
                    </strong>
                    {(() => {
                      const structure = termStructures.find(
                        (s) => s.id === selectedTermStructure
                      );
                      if (!structure || !structure.termDates) return null;

                      return (
                        <div style={{ marginTop: "10px" }}>
                          <div
                            style={{
                              marginBottom: "10px",
                              padding: "8px 12px",
                              background: "#28a745",
                              color: "white",
                              borderRadius: "5px",
                              fontSize: "14px",
                              fontWeight: "bold",
                            }}
                          >
                            {structure.lessonsPerTerm || 10} lessons per term
                          </div>
                          {Object.keys(structure.termDates).map((termKey) => {
                            const term = structure.termDates[termKey];
                            const termNum = termKey.replace("term", "");
                            return (
                              <div
                                key={termKey}
                                style={{
                                  padding: "5px 0",
                                  fontSize: "14px",
                                  color: "#333",
                                }}
                              >
                                <strong>Term {termNum}:</strong> {term.start} to{" "}
                                {term.end}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "5px",
                  padding: "15px",
                }}
              >
                <strong style={{ color: "#856404" }}>
                  ÃƒÂ¢Ã…Â¡ ÃƒÂ¯Ã‚Â¸Ã‚Â No Term Structures Available
                </strong>
                <p
                  style={{
                    margin: "10px 0 0 0",
                    color: "#856404",
                    fontSize: "14px",
                  }}
                >
                  Please create a term structure template first before adding
                  schools. Go to Dashboard Ã¢â‚¬â„¢ Term Structure Setup to create one.
                </p>
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Admin Fee & Payment Structure</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Annual Admin Fee (R) *</label>
                <input
                  type="number"
                  name="adminFee"
                  value={formData.adminFee}
                  onChange={handleChange}
                  required
                  placeholder="160"
                />
                <small>
                  Charged once per year with Term 1 invoice (can be waived per
                  student)
                </small>
              </div>

              <div className="form-group">
                <label>Instalments per Term *</label>
                <select
                  name="instalmentType"
                  value={formData.instalmentType}
                  onChange={handleChange}
                  required
                >
                  <option value="3">3 Instalments per Term</option>
                  <option value="4">4 Instalments per Term</option>
                </select>
                <small>
                  {formData.instalmentType === "3"
                    ? `Term fee split into 3 payments (e.g., R600 ÃƒÆ’Ã‚Â· 3 = R200 each)`
                    : `Term fee split into 4 payments (e.g., R600 ÃƒÆ’Ã‚Â· 4 = R150 each)`}
                </small>
              </div>
            </div>

            <div
              style={{
                background: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "5px",
                padding: "15px",
                marginTop: "15px",
              }}
            >
              <strong>ℹ️ Payment Structure Example:</strong>
              <p
                style={{
                  margin: "5px 0 0 0",
                  fontSize: "14px",
                  color: "#856404",
                }}
              >
                {formData.instalmentType === "3"
                  ? `With ${
                      formData.customTermType || "4"
                    } terms and 3 instalments per term = ${
                      (formData.customTermType || 4) * 3
                    } payments per year`
                  : `With ${
                      formData.customTermType || "4"
                    } terms and 4 instalments per term = ${
                      (formData.customTermType || 4) * 4
                    } payments per year`}
              </p>
            </div>
          </div>

          <div className="form-section">
            <h2>Lessons Per Term *</h2>

            <div className="form-group">
              <label>Number of Lessons Per Term *</label>
              <input
                type="number"
                name="lessonsPerTerm"
                value={formData.lessonsPerTerm}
                onChange={handleChange}
                required
                min="3"
                max="20"
                placeholder="8"
              />
              <small>
                {selectedTermStructure &&
                  termStructures.length > 0 &&
                  (() => {
                    const structure = termStructures.find(
                      (s) => s.id === selectedTermStructure
                    );
                    return structure?.lessonsPerTerm
                      ? `Default from selected term structure: ${structure.lessonsPerTerm} lessons. You can override if this school differs.`
                      : "Typical: Public schools = 8 lessons/term, Private schools = 10-11 lessons/term";
                  })()}
                {!selectedTermStructure &&
                  "Typical: Public schools = 8 lessons/term, Private schools = 10-11 lessons/term"}
              </small>
            </div>

            <div
              style={{
                background: "#e7f3ff",
                border: "1px solid #4169E1",
                borderRadius: "5px",
                padding: "15px",
                marginTop: "10px",
              }}
            >
              <strong style={{ color: "#4169E1" }}>💡 Why this matters:</strong>
              <p
                style={{ margin: "5px 0 0 0", fontSize: "14px", color: "#333" }}
              >
                This number is used for pro-rata calculations when students join
                mid-term. It ensures accurate fee calculations based on lessons
                attended.
              </p>
            </div>
          </div>

          <div className="form-section">
            <h2>📅 Lesson Schedule *</h2>
            <p className="section-note">
              Set the regular weekly lesson day and time for this school
            </p>

            <div className="form-row">
              <div className="form-group">
                <label>Lesson Day *</label>
                <select
                  name="lessonDay"
                  value={formData.lessonDay}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Day --</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>
              </div>

              <div className="form-group">
                <label>Lesson Time *</label>
                <input
                  type="time"
                  name="lessonTime"
                  value={formData.lessonTime}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div
              style={{
                background: "#e7f3ff",
                border: "1px solid #4169E1",
                borderRadius: "5px",
                padding: "15px",
                marginTop: "10px",
              }}
            >
              <strong style={{ color: "#4169E1" }}>💡 Example:</strong>
              <p style={{ margin: "5px 0 0 0", fontSize: "14px", color: "#333" }}>
                ABC School: Every Monday at 10:00 | XYZ School: Every Wednesday at 14:00
              </p>
            </div>
          </div>

          <div className="form-section">
            <h2>🏫 School Commission (Optional)</h2>
            <p className="section-note">
              If you pay this school a commission, configure it here. It will be used in the School Commission Report.
            </p>

            <div className="form-group">
              <label>Commission Type</label>
              <select
                name="commissionType"
                value={formData.commissionType}
                onChange={handleChange}
              >
                <option value="none">No Commission</option>
                <option value="percentage">Percentage (%) of fees collected</option>
                <option value="fixed">Fixed Amount (R) per period</option>
              </select>
            </div>

            {formData.commissionType !== "none" && (
              <div className="form-row" style={{ marginTop: "12px" }}>
                <div className="form-group">
                  <label>
                    {formData.commissionType === "percentage"
                      ? "Commission Percentage (%)"
                      : "Fixed Commission Amount (R)"}
                  </label>
                  <input
                    type="number"
                    name="commissionValue"
                    value={formData.commissionValue}
                    onChange={handleChange}
                    min="0"
                    step={formData.commissionType === "percentage" ? "0.1" : "1"}
                    placeholder={
                      formData.commissionType === "percentage" ? "e.g., 10" : "e.g., 500"
                    }
                  />
                  <small>
                    {formData.commissionType === "percentage"
                      ? "Enter the % of fees received to pay this school (e.g., 10 for 10%)"
                      : "Enter the fixed Rand amount to pay this school per period"}
                  </small>
                </div>

                <div className="form-group" style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      background: formData.commissionType === "percentage" ? "#e8f5e9" : "#fff3e0",
                      border: `1px solid ${formData.commissionType === "percentage" ? "#4CAF50" : "#FF9800"}`,
                      borderRadius: "8px",
                      padding: "14px",
                      marginTop: "24px",
                      fontSize: "14px",
                      color: "#333",
                    }}
                  >
                    {formData.commissionType === "percentage" ? (
                      <>
                        <strong style={{ color: "#2e7d32" }}>📊 Example:</strong>
                        <br />
                        If you collect R{(600).toFixed(0)} from 5 paying students (R3,000 total)
                        and commission is {formData.commissionValue || "X"}%:
                        <br />
                        <strong>Commission = R{(3000 * (parseFloat(formData.commissionValue) || 0) / 100).toFixed(2)}</strong>
                      </>
                    ) : (
                      <>
                        <strong style={{ color: "#e65100" }}>💰 Fixed Amount:</strong>
                        <br />
                        You will pay this school{" "}
                        <strong>R{parseFloat(formData.commissionValue || 0).toFixed(2)}</strong>{" "}
                        regardless of how many students attend.
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h2>Additional Notes (Optional)</h2>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="e.g., Lessons on Wednesdays only, Special requirements, etc."
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading
                ? isEditMode
                  ? "Updating School..."
                  : "Adding School..."
                : isEditMode
                ? "Update School"
                : "Add School"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddSchool;